<template>
  <div :class="$style.wrapper">
    <div :class="$style.column">
      <div :class="$style.title">Content</div>
      <div :class="$style.content">
        <div :class="$style.formField">
          <label for="offerTitle">Offer title</label>
          <InputText
            id="offerTitle"
            v-model="offerTitle"
            :class="$style.textInput"
            placeholder="Offer title"
            :disabled="!isOfferEditable"
          />
        </div>
        <div :class="[$style.formField]">
          <label for="highlightTexts">Highlight texts</label>
          <AutoComplete
            v-model="highlightTextsRef"
            :placeholder="highlightTextsRef && highlightTextsRef.length > 0 ? '' : 'Type any highlight text'"
            multiple
            :disabled="!isOfferEditable"
            :typeahead="false"
            :pt="{
              root: {
                style: {
                  width: '100%',
                  padding: '0.15rem 0.3rem',
                },
              },
              container: {
                style: {
                  width: '100%',
                  'max-height': '170px',
                  'font-size': '12px',
                  overflow: 'scroll',
                },
              },
              input: {
                style: {
                  width: '100%',
                  height: '32px',
                  'font-size': '12px',
                },
              },
              token: {
                style: {
                  margin: '0.2rem 0.1rem',
                },
              },
            }"
            @value-change="handleHighlightTextsUpdate"
          />
          <label :class="$style.highlightTextsInfo">Separate strings using a comma and press enter</label>
        </div>
      </div>
    </div>
    <div :class="$style.column">
      <div :class="$style.title">Images</div>
      <div :class="$style.content">
        <div :class="$style.formField">
          <label>Offer image</label>
          <OfferImageInput :image="image" :is-editable="isOfferEditable" @update="handleOfferImageUpdate" />
        </div>
        <div :class="$style.formField">
          <label>Menu badge</label>
          <OfferBadgeDropDown
            v-if="menuBadge"
            :menu-badge-icon="menuBadge.menuBadgeIcon"
            :display-count-down-in-menu-badge="menuBadge.displayCountDownInMenuBadge"
            :display-menu-badge="menuBadge.displayMenuBadge"
            :is-editable="isOfferEditable"
            @update="handleOfferMenuBadgeUpdate"
          />
          <div v-if="displayCountDown" :class="$style.displayCountdown">
            <Checkbox
              v-if="menuBadge"
              :model-value="menuBadge.displayCountDownInMenuBadge"
              :disabled="!menuBadge.displayMenuBadge"
              :binary="true"
              name="displayCountdownInMenuBadge"
              :pt="{
                box: {
                  class: $style.checkbox,
                },
                icon: {
                  style: {
                    height: '10px',
                  },
                },
              }"
              @update:model-value="handleDisplayCountdownInMenuBadgeChange"
            />
            <label for="displayCountdownInMenuBadge">Show countdown in badge</label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import AutoComplete from "primevue/autocomplete";
import Checkbox from "primevue/checkbox";
import InputText from "primevue/inputtext";
import type { PropType } from "vue";
import { computed, ref, watch } from "vue";
import OfferBadgeDropDown from "./OfferBadgeDropDown.vue";
import OfferImageInput from "./OfferImageInput.vue";

const emit = defineEmits([
  "updateMenuBadge",
  "updateImage",
  "updateTitle",
  "updateHighlightTexts",
  "updateDisplayCountdownInMenuBadge",
]);

interface MenuBadge {
  displayCountDownInMenuBadge?: boolean;
  displayMenuBadge?: boolean;
  menuBadgeIcon?: string;
}

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  menuBadge: {
    type: Object as PropType<MenuBadge>,
  },
  displayCountDown: {
    type: Boolean,
  },
  image: {
    type: String,
  },
  status: {
    type: String,
    required: true,
  },
  isWipVersion: {
    type: Boolean,
    required: true,
  },
  highlightTexts: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const isOfferEditable = computed(() => props.status === "DRAFT" && props.isWipVersion);

const highlightTextsRef = ref<string[]>(props.highlightTexts || []);

const hadAnythingChanged = (newItem: MenuBadge, oldItem: MenuBadge) =>
  JSON.stringify(newItem) !== JSON.stringify(oldItem);

const handleTitleUpdate = useDebounceFn(async (value: string) => {
  emit("updateTitle", value);
}, 1000);

const offerTitle = computed({
  get: () => props.title,
  set: (value) => {
    handleTitleUpdate(value);
  },
});

const handleOfferMenuBadgeUpdate = (menuBadge: MenuBadge) => {
  const oldValues = {
    displayCountDownInMenuBadge: props.menuBadge?.displayCountDownInMenuBadge,
    displayMenuBadge: props.menuBadge?.displayMenuBadge,
    menuBadgeIcon: props.menuBadge?.menuBadgeIcon,
  };
  if (hadAnythingChanged(menuBadge, oldValues)) {
    emit("updateMenuBadge", menuBadge);
  }
};

const handleOfferImageUpdate = (image: string) => {
  emit("updateImage", image);
};

const handleHighlightTextsUpdate = (value: string[]) => {
  const newValue = value
    .map((item) => item.split(","))
    .reduce((acc, val) => acc.concat(val), [])
    .map((item) => item.trim())
    .filter((item) => item !== "");

  const uniqueValues = Array.from(new Set(newValue));

  highlightTextsRef.value = uniqueValues;
  emit("updateHighlightTexts", uniqueValues);
};

const handleDisplayCountdownInMenuBadgeChange = (newValue: Event) => {
  emit("updateDisplayCountdownInMenuBadge", newValue);
};

watch(
  () => props.highlightTexts,
  (newValue) => {
    highlightTextsRef.value = newValue;
  },
  {
    immediate: true,
  }
);
</script>

<style module lang="scss">
.wrapper {
  display: flex;
  padding: 1rem;
  width: 100%;

  .column {
    width: 50%;
    padding: 1rem;
    text-align: center;

    .title {
      font-size: 12px;
      color: var(--vd-color-text-light-2);
      margin-bottom: 2rem;
    }

    .content {
      display: flex;
      width: 100%;
      gap: 1rem;

      .formField {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
        width: 50%;

        label {
          font-size: 12px;
          color: var(--vd-color-text-default);
        }
        .textInput {
          border: 1px solid var(--vd-color-neutral-light-2);
          font-size: 12px;
          color: var(--vd-color-text-dark);
          width: 100%;
          border-radius: 8px;
          height: 32px;
        }

        .highlightTextsInfo {
          font-size: 11px;
          color: var(--vd-color-text-light-2);
          text-align: left;
        }

        .displayCountdown {
          label {
            line-height: 28px;
            font-size: 12px;
          }
        }

        .checkbox {
          width: 16px;
          height: 16px;
          border: 1px solid var(--vd-color-neutral-light-2);
          border-radius: 4px;
        }
      }
    }
  }
}
.chipsField {
  width: 100%;
}
</style>
